import React, { useEffect, useState, useCallback } from 'react';
import accessLocalStorage from '../../services/accessLocalStorage';
import './config-styles.css';

const getApiBase = async () => {
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};

function QueuesConfig() {
  const [loading, setLoading] = useState(true);
  const [queues, setQueues] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingQueue, setEditingQueue] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    strategy: 'ringall',
    timeout: 30,
    wrapupTime: 10,
    maxWaitTime: 300,
    serviceLevelThreshold: 20,
  });

  const strategies = [
    { value: 'ringall', label: 'Ring All - Toca em todos' },
    { value: 'roundrobin', label: 'Round Robin - Rotativo' },
    { value: 'leastrecent', label: 'Least Recent - Menos recente' },
    { value: 'fewestcalls', label: 'Fewest Calls - Menos chamadas' },
    { value: 'random', label: 'Random - Aleatório' },
    { value: 'rrmemory', label: 'RR Memory - Rotativo com memória' },
  ];

  const fetchQueues = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const response = await fetch(`${apiBase}/v2/callcenter/queues`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setQueues(data.queues || data || []);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const url = editingQueue
        ? `${apiBase}/v2/callcenter/queues/${encodeURIComponent(editingQueue.queueKey)}`
        : `${apiBase}/v2/callcenter/queues`;

      const method = editingQueue ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-tenant-id': tenantId,
        },
        body: JSON.stringify({
          ...formData,
          queueKey: formData.name.toLowerCase().replace(/\s+/g, '-'),
        }),
      });

      if (response.ok) {
        setShowModal(false);
        setEditingQueue(null);
        resetForm();
        fetchQueues();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Erro ao salvar fila: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      console.error('Erro ao salvar fila:', e);
      alert('Erro ao salvar fila. Verifique sua conexão.');
    }
  };

  const handleDelete = async (queueKey) => {
    if (!window.confirm('Tem certeza que deseja excluir esta fila?')) return;

    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const response = await fetch(`${apiBase}/v2/callcenter/queues/${encodeURIComponent(queueKey)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
      });

      if (response.ok) {
        fetchQueues();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Erro ao excluir fila: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      console.error('Erro ao excluir fila:', e);
      alert('Erro ao excluir fila. Verifique sua conexão.');
    }
  };

  const openEditModal = (queue) => {
    setEditingQueue(queue);
    setFormData({
      name: queue.name || '',
      description: queue.description || '',
      strategy: queue.strategy || 'ringall',
      timeout: queue.timeout || 30,
      wrapupTime: queue.wrapupTime || 10,
      maxWaitTime: queue.maxWaitTime || 300,
      serviceLevelThreshold: queue.serviceLevelThreshold || 20,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      strategy: 'ringall',
      timeout: 30,
      wrapupTime: 10,
      maxWaitTime: 300,
      serviceLevelThreshold: 20,
    });
  };

  const openNewModal = () => {
    setEditingQueue(null);
    resetForm();
    setShowModal(true);
  };

  return (
    <div className="ccv2-config-container">
      <div className="ccv2-config-header">
        <div className="ccv2-config-title-area">
          <h1>Configuração de Filas</h1>
          <p>Gerencie as filas do Call Center</p>
        </div>
        <button className="ccv2-btn ccv2-btn-primary" onClick={openNewModal}>
          + Nova Fila
        </button>
      </div>

      {loading ? (
        <div className="ccv2-config-loading">
          <div className="ccv2-spinner"></div>
          <p>Carregando filas...</p>
        </div>
      ) : (
        <div className="ccv2-config-card">
          <div className="ccv2-config-table-wrapper">
            <table className="ccv2-config-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Estratégia</th>
                  <th>Timeout</th>
                  <th>SLA</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {queues.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="ccv2-empty-row">
                      Nenhuma fila cadastrada
                    </td>
                  </tr>
                ) : (
                  queues.map((queue) => (
                    <tr key={queue.queueKey || queue.id_callcenter_queue}>
                      <td className="ccv2-cell-name">{queue.name}</td>
                      <td>{queue.description || '-'}</td>
                      <td>
                        <span className="ccv2-badge">{queue.strategy}</span>
                      </td>
                      <td>{queue.timeout}s</td>
                      <td>{queue.serviceLevelThreshold}s</td>
                      <td className="ccv2-cell-actions">
                        <button
                          className="ccv2-btn-icon ccv2-btn-edit"
                          onClick={() => openEditModal(queue)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          className="ccv2-btn-icon ccv2-btn-delete"
                          onClick={() => handleDelete(queue.queueKey)}
                          title="Excluir"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="ccv2-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ccv2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ccv2-modal-header">
              <h2>{editingQueue ? 'Editar Fila' : 'Nova Fila'}</h2>
              <button className="ccv2-modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="ccv2-modal-body">
                <div className="ccv2-form-group">
                  <label>Nome da Fila</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Suporte Técnico"
                    required
                  />
                </div>

                <div className="ccv2-form-group">
                  <label>Descrição</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição opcional"
                  />
                </div>

                <div className="ccv2-form-group">
                  <label>Estratégia de Distribuição</label>
                  <select
                    value={formData.strategy}
                    onChange={(e) => setFormData({ ...formData, strategy: e.target.value })}
                  >
                    {strategies.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Timeout (segundos)</label>
                    <input
                      type="number"
                      value={formData.timeout}
                      onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
                      min="5"
                      max="120"
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>Wrapup (segundos)</label>
                    <input
                      type="number"
                      value={formData.wrapupTime}
                      onChange={(e) => setFormData({ ...formData, wrapupTime: parseInt(e.target.value) })}
                      min="0"
                      max="300"
                    />
                  </div>
                </div>

                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Tempo Máx. Espera (segundos)</label>
                    <input
                      type="number"
                      value={formData.maxWaitTime}
                      onChange={(e) => setFormData({ ...formData, maxWaitTime: parseInt(e.target.value) })}
                      min="30"
                      max="3600"
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>SLA Threshold (segundos)</label>
                    <input
                      type="number"
                      value={formData.serviceLevelThreshold}
                      onChange={(e) => setFormData({ ...formData, serviceLevelThreshold: parseInt(e.target.value) })}
                      min="5"
                      max="120"
                    />
                  </div>
                </div>
              </div>

              <div className="ccv2-modal-footer">
                <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="ccv2-btn ccv2-btn-primary">
                  {editingQueue ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default QueuesConfig;
